import React from 'react';

import { CustomCard } from '@/modules/Dashboard/DashboardPage/Components/MetricComponents';

interface Subscription {
  id: string;
  stripeSubscriptionId: string;
  status: string;
  currency: string;
  cancel_at_period_end: boolean;
  canceled_at?: number;
  cancel_at?: number;
  trial_start?: number | null;
  trial_end?: number | null;
  collection_method: string;
  items?: {
    data?: Array<{
      id: string;
      quantity: number;
      price?: {
        id: string;
        unit_amount: number;
        currency: string;
        recurring?: {
          interval: string;
          interval_count: number;
        };
      };
      plan?: {
        id: string;
        amount: number;
        currency: string;
        interval: string;
        interval_count: number;
      };
      current_period_start?: number;
      current_period_end?: number;
    }>;
  };
  stripeCreatedAt?: number;
}

interface SubscriptionsSectionProps {
  subscriptions?: Subscription[];
}

const SubscriptionsSection: React.FC<SubscriptionsSectionProps> = ({ subscriptions = [] }) => {
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount / 100);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-custom-green/10 text-custom-green border-custom-green/20';
      case 'canceled':
      case 'cancelled':
        return 'bg-custom-red/10 text-custom-red border-custom-red/20';
      case 'past_due':
        return 'bg-custom-yellow/10 text-custom-yellow border-custom-yellow/20';
      case 'trialing':
        return 'bg-custom-blue/10 text-custom-blue border-custom-blue/20';
      default:
        return 'bg-custom-darkSilver/10 text-custom-darkSilver border-custom-darkSilver/20';
    }
  };

  return (
    <CustomCard title="Subscriptions">
      <div className="mt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-custom-darkSilver">
            <span>
              {subscriptions.length} {subscriptions.length === 1 ? 'Subscription' : 'Subscriptions'}
            </span>
          </div>
        </div>

        <div className="overflow-auto">
          {subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-custom-darkSilver font-medium">No subscriptions found</div>
              <div className="text-custom-darkSilver text-sm">This member has no active subscriptions</div>
            </div>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((subscription) => {
                const firstItem = subscription.items?.data?.[0];
                const plan = firstItem?.plan;
                const price = firstItem?.price;
                // Plan has 'amount', price has 'unit_amount'
                const amount = plan?.amount || price?.unit_amount || 0;
                const currency = plan?.currency || price?.currency || subscription.currency || 'usd';
                // Plan has 'interval' directly, price has it in 'recurring.interval'
                const interval = plan?.interval || price?.recurring?.interval || 'month';
                const intervalCount = plan?.interval_count || price?.recurring?.interval_count || 1;

                return (
                  <div key={subscription.id} className="border border-custom-lightSilver rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span
                            className={`text-xs px-2 py-1 rounded-md border ${getStatusColor(subscription.status)}`}
                          >
                            {subscription.status}
                          </span>
                          {subscription.cancel_at_period_end && (
                            <span className="text-xs px-2 py-1 rounded-md bg-custom-yellow/10 text-custom-yellow border border-custom-yellow/20">
                              Cancels at period end
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-semibold text-custom-black mb-1">
                          {formatCurrency(amount, currency)} /{' '}
                          {intervalCount === 1 ? interval : `${intervalCount} ${interval}s`}
                        </div>
                        <div className="text-xs text-custom-darkSilver">ID: {subscription.stripeSubscriptionId}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {firstItem?.current_period_start && firstItem?.current_period_end && (
                        <>
                          <div>
                            <div className="text-xs text-custom-darkSilver mb-1">Current Period</div>
                            <div className="text-custom-black">
                              {formatDate(firstItem.current_period_start)} - {formatDate(firstItem.current_period_end)}
                            </div>
                          </div>
                        </>
                      )}
                      {subscription.trial_start && subscription.trial_end && (
                        <div>
                          <div className="text-xs text-custom-darkSilver mb-1">Trial Period</div>
                          <div className="text-custom-black">
                            {formatDate(subscription.trial_start)} - {formatDate(subscription.trial_end)}
                          </div>
                        </div>
                      )}
                      {subscription.cancel_at && (
                        <div>
                          <div className="text-xs text-custom-darkSilver mb-1">Cancels On</div>
                          <div className="text-custom-black">{formatDate(subscription.cancel_at)}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-xs text-custom-darkSilver mb-1">Collection Method</div>
                        <div className="text-custom-black capitalize">{subscription.collection_method}</div>
                      </div>
                    </div>

                    {subscription.stripeCreatedAt && (
                      <div className="mt-3 pt-3 border-t border-custom-lightSilver">
                        <div className="text-xs text-custom-darkSilver">
                          Created: {formatDate(subscription.stripeCreatedAt)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </CustomCard>
  );
};

export default SubscriptionsSection;
